import { supabase } from './supabase';
import type { SubscriptionPlan, RazorpaySuccessData } from './types/subscription';
import { env } from './env';

export type { SubscriptionPlan, RazorpaySuccessData };

export async function getPaymentPlans(): Promise<SubscriptionPlan[]> {
  try {
    const { data, error } = await supabase
      .from('payment_plans')
      .select('*')
      .order('price', { ascending: true });
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching payment plans:', error);
    throw error;
  }
}

export const createRazorpayOrder = async (
  plan: SubscriptionPlan,
  userPhone: string,
  userName: string,
  userEmail?: string
): Promise<{orderId: string, amount: number}> => {
  try {
    console.log('Creating order for plan:', plan.plan_id);
    
    // Call Supabase Edge Function to create order
    const { data, error } = await supabase.functions.invoke('create-order', {
      body: {
        plan,
        userPhone,
        userName,
        userEmail
      }
    });

    if (error) {
      console.error('Edge function error:', error);
      throw new Error(`Failed to create order: ${error.message}`);
    }
    
    if (!data || !data.id) {
      throw new Error('Invalid response from order creation API');
    }
    
    console.log('Order created successfully:', data.id);
    
    return {
      orderId: data.id,
      amount: data.amount
    };
  } catch (error) {
    console.error('Error creating Razorpay order:', error);
    throw error;
  }
};

export const saveSubscription = async (
  userPhone: string, 
  planId: string, 
  paymentId: string,
  orderId: string,
  amount: number
) => {
  try {
    console.log('Saving subscription directly without verification:', {
      userPhone,
      planId,
      paymentId,
      orderId,
      amount
    });

    // Skip verification and directly save the subscription
    const { data, error } = await supabase.rpc(
      'create_subscription',
      {
        p_user_phone: userPhone,
        p_plan_id: planId,
        p_payment_id: paymentId,
        p_order_id: orderId,
        p_amount_paid: amount
      }
    );

    if (error) {
      console.error('Error saving subscription:', error);
      throw error;
    }
    
    console.log('Subscription saved successfully');
    return data;
  } catch (error) {
    console.error('Error saving subscription:', error);
    throw error;
  }
};

export const checkSubscriptionStatus = async (userPhone: string) => {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_phone', userPhone)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1);
    
    if (error) throw error;
    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    console.error('Error checking subscription:', error);
    return null;
  }
};